import { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  animate,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const ScoreCard = () => {
  const score = useMotionValue(524);
  const smooth = useSpring(score, { stiffness: 40, damping: 18 });
  const display = useTransform(smooth, (v) => Math.round(v));
  const barScale = useTransform(smooth, [524, 781], [0.18, 0.94]);

  useEffect(() => {
    const controls = animate(score, 781, { delay: 1.8, duration: 2.6, ease: [0.16, 1, 0.3, 1] });
    return controls.stop;
  }, [score]);

  return (
    <div className="bg-black/50 backdrop-blur-2xl border border-white/10 p-7 w-[300px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        Client trajectory
      </p>
      <div className="mt-4 flex items-end gap-2">
        <motion.span className="font-serif text-6xl text-gold leading-none">
          {display}
        </motion.span>
        <span className="font-mono text-[10px] text-neutral-500 mb-1.5">/ 850</span>
      </div>
      <div className="mt-5 h-px bg-white/10 relative overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gold origin-left"
          style={{ scaleX: barScale, width: "100%" }}
        />
      </div>
      <p className="mt-4 text-xs text-neutral-400 font-light leading-relaxed">
        Average elevation across coached clients in their first two seasons.
      </p>
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img
          src="https://images.pexels.com/photos/31622917/pexels-photo-31622917.jpeg"
          alt="Golden architectural ascent"
          className="w-full h-[115%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(10,10,10,0.9),transparent_60%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full pt-36 pb-20 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <MaskedLine delay={0.5}>
              <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold" data-testid="hero-overline">
                Elevated Apex Co.
              </span>
            </MaskedLine>
            <h1 className="mt-8 font-serif font-medium tracking-tighter leading-[0.95] text-white" data-testid="hero-headline">
              <MaskedLine delay={0.65} className="text-5xl sm:text-7xl lg:text-[6.5rem]">
                Your score is not
              </MaskedLine>
              <MaskedLine delay={0.8} className="text-5xl sm:text-7xl lg:text-[6.5rem]">
                your ceiling.
              </MaskedLine>
              <MaskedLine delay={0.95} className="text-5xl sm:text-7xl lg:text-[6.5rem] italic text-gold">
                It's your launchpad.
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
              className="mt-10 max-w-xl text-base sm:text-lg text-neutral-300 font-light leading-relaxed"
              data-testid="hero-subcopy"
            >
              Education-first credit coaching for people who refuse to be defined
              by a three-digit number. We decode your report, build your action
              plan, and walk with you — point by point — toward funding readiness.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
              className="mt-12 flex flex-wrap items-center gap-5"
            >
              <button
                onClick={() => scrollToSection("contact")}
                className="group flex items-center gap-3 bg-gold text-black text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:bg-white transition-colors duration-300"
                data-testid="hero-consultation-cta"
              >
                Book a Free Consultation
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                onClick={() => scrollToSection("academy")}
                className="group flex items-center gap-3 border border-white/25 text-white text-xs font-medium uppercase tracking-[0.15em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
                data-testid="hero-academy-cta"
              >
                Explore the Academy
              </button>
            </motion.div>
          </div>

          <motion.div
            className="lg:col-span-4 hidden lg:flex justify-end"
            style={{ y: cardY }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.7, ease: EASE }}
          >
            <ScoreCard />
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="relative z-10 border-t border-white/10"
        data-testid="hero-stats-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["1,400+", "Clients coached"],
            ["9 yrs", "Education practice"],
            ["120 pts", "Avg. first-year lift"],
            ["100%", "Education-first, always"],
          ].map(([num, label], i) => (
            <div key={i} className="flex items-baseline gap-3">
              <span className="font-serif text-2xl text-gold">{num}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToSection("services")}
        className="absolute bottom-28 right-8 z-10 hidden lg:flex flex-col items-center gap-2 text-neutral-500 hover:text-gold transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut", delay: 2.5 }}
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to services"
      >
        <ArrowDown className="w-4 h-4" />
      </motion.button>
    </section>
  );
};

export default Hero;
